import React from "react";
import { Box, Container, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import Icon1 from "../../assets/choose_icon1.png";
import Icon2 from "../../assets/choose_icon2.png";
import { HeadingText } from "../../utils/CustomTagComponents";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const WhyChooseGlap = ({ title, desc, features }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // viewport trigger
  const [sectionRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  // container stagger animation
  const containerVariant = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.18
      }
    }
  };

  // card animation
  const cardVariant = {
    hidden: {
      opacity: 0,
      y: 40,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const getResponsiveTitle = (html) => {
    if (!html) return "";
    if (!isMobile) return html;

    // Basic simplification: remove inline styles and reduce font sizes
    let clean = html
      // ✅ remove full style attribute
      // .replace(/style="[^"]*"/gi, "")
      .replace(
        /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
        "<$1$2$3>"
      )

      // ✅ extra safety (if style not removed)
      .replace(/font-size:\s*\d+px;?/gi, "")

      // ✅ clean font-weight
      .replace(/font-weight:\s*900;?/gi, "font-weight: 700");

    return clean;
  };

  // ✅ CLEAN HTML
  const cleanHTML = (html) => {
    if (!html) return "";
    return html
      // .replace(/style="[^"]*"/gi, "")
      .replace(
        /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
        "<$1$2$3>"
      )
      .replace(/<p><br><\/p>/gi, "")
      .replace(/&nbsp;/gi, " ");
  };

  const getResponsiveHtml = (html) => {
    if (!html) return "";

    let clean = html
      // .replace(/style="[^"]*"/gi, "")
      .replace(
        /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
        "<$1$2$3>"
      )
      .replace(/&nbsp;/gi, " ")
      .replace(/\u00A0/gi, " ")
      .replace(/<br\s*\/?>/gi, "")
      .replace(/\[empty_line\]/gi, "<br />");

    if (!isMobile) return clean;

    return clean.replace(/font-weight:\s*900/gi, "font-weight: 700").replace(/&nbsp;/gi, " ").replace(/\u00A0/gi, " ").replace(/\[empty_line\]/gi, "<br />");
  };

  return (
    <Container maxWidth="xl" sx={{
      overflow: 'hidden',
      py: { xs: 2, sm: 2, md: 3 },
      px: { xs: 3, sm: 4, md: 6 },
      paddingLeft: { xs: "24px !important", md: "48px !important" },
      paddingRight: { xs: "24px !important", md: "48px !important" },
    }}>
      {title && (
        <Box
          sx={{
            display: "inline-block",
            position: "relative",
            mb: 2,

            "&::after": {
              content: '""',
              position: "absolute",
              left: 0,
              bottom: -5,
              width: "200px",
              height: "2px",
              backgroundColor: "#fd0000",
              opacity: "0.5"
            },
          }}
        >
          <HeadingText
            sx={{
              fontSize: { xs: "22px", sm: "26px", md: "30px" },
              lineHeight: '1.3 !important',
              fontWeight: 700,
            }}
            dangerouslySetInnerHTML={{ __html: getResponsiveTitle(title) }}
          />
        </Box>
      )}
      <Typography
        sx={{
          fontSize: { xs: 14, sm: 16 },
          fontWeight: 400,
          color: "#000000",
          lineHeight: "24px",
          mb: 2,
        }}
        dangerouslySetInnerHTML={{
          __html: cleanHTML(getResponsiveHtml(desc)),
        }}
      >
      </Typography>

      <Grid container spacing={2} component={motion.div}
        ref={sectionRef}
        variants={containerVariant}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}>
        {features.map((item, index) => (
          <Grid
            component={motion.div}
            variants={cardVariant}
            size={{ xs: 12, sm: 6, md: 4 }}
            key={index}
            sx={{ display: "flex" }}
          >
            <Box
              sx={{
                backgroundColor: "#002045",
                px: 2,
                py: 2,
                borderRadius: "20px",
                height: "100%",
                transition: "all 0.3s ease",

                "&:hover": {
                  backgroundColor: "#003a7a",
                  transform: "translateY(-5px)",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
                },
              }}
            >
              <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: "10px",
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={item.image || Icon1}
                    alt={item.key || "icon"}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = Icon1;
                    }}
                    sx={{
                      width: { xs: 28, sm: 28, md: 28 },
                      height: { xs: 28, sm: 28, md: 28 },
                      objectFit: "contain",
                    }}
                  />
                </Box>
                {/* </Box> */}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontSize: { xs: 18, sm: 18 },
                    fontWeight: 700,
                    color: "#ffffff",
                  }}
                >
                  {item.key}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: 14, sm: 16 },
                  fontWeight: 400,
                  color: "#ffffff",
                  pt: 1,
                }}
              >
                {item.value}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default WhyChooseGlap;
